import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile captcha widget (G6). Loads the script once, renders
 * explicitly, and reports the solved token (or null on error/expiry) via
 * onToken. Rendered only when the server advertises a site key, so the whole
 * thing stays dormant until captcha is configured.
 */
interface TurnstileApi {
	render: (
		el: HTMLElement,
		opts: {
			sitekey: string;
			callback: (token: string) => void;
			"error-callback"?: () => void;
			"expired-callback"?: () => void;
			theme?: "auto" | "light" | "dark";
		},
	) => string;
	remove: (id: string) => void;
}

declare global {
	interface Window {
		turnstile?: TurnstileApi;
	}
}

const SCRIPT_SRC =
	"https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
	if (typeof window !== "undefined" && window.turnstile)
		return Promise.resolve();
	if (scriptPromise) return scriptPromise;
	scriptPromise = new Promise<void>((resolve, reject) => {
		const s = document.createElement("script");
		s.src = SCRIPT_SRC;
		s.async = true;
		s.defer = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error("turnstile script failed to load"));
		document.head.appendChild(s);
	});
	return scriptPromise;
}

export function TurnstileWidget({
	siteKey,
	onToken,
}: {
	siteKey: string;
	onToken: (token: string | null) => void;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const widgetId = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		loadTurnstileScript()
			.then(() => {
				if (cancelled || !ref.current || !window.turnstile) return;
				widgetId.current = window.turnstile.render(ref.current, {
					sitekey: siteKey,
					callback: (token: string) => onToken(token),
					"error-callback": () => onToken(null),
					"expired-callback": () => onToken(null),
				});
			})
			.catch(() => {
				// Script blocked → token stays null, so submit stays disabled.
			});
		return () => {
			cancelled = true;
			if (widgetId.current && window.turnstile) {
				try {
					window.turnstile.remove(widgetId.current);
				} catch {
					/* widget already gone */
				}
			}
		};
	}, [siteKey, onToken]);

	return <div ref={ref} className="flex justify-center" />;
}
